import { alpha, createTheme, ThemeOptions } from '@mui/material';
import colors from './colors.module.scss';

const partialTheme = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 601,
			md: 961,
			lg: 1367,
			xl: 1537,
		},
	},
	custom: {
		navbarHeight: '50px',
	},
	palette: {
		primary: {
			main: colors['primary'], // '#FE7223',
			contrastText: colors['pure-white'], // '#FFFFFF',
		},
		secondary: {
			main: colors['secondary'], // '#FEC123',
			contrastText: colors['pure-white'], // '#FFFFFF',
		},
		text: {
			primary: colors['main-text'], //#635533
			secondary: colors['primary-dark-blue'], //#252f35,
			disabled: colors['dark-grey'], //#8F8F8F
		},
		highlighted: {
			primary: colors['primary-highlighted'], // '#FFFAED',
			secondary: colors['ternary-light-grey'], // '#F9F9F9',
			disabled: colors['pure-black'], // '#000000',
		},
		green: {
			main: colors['primary-green'], // '#18A900',
			light: '',
			dark: '',
			contrastText: '',
		},
		borders: {
			primary: colors['disabled-grey'], // '#D9D9D9',
			secondary: colors['disabled-grey'], // '#D9D9D9',
			disabled: colors['disabled-grey'], // '#D9D9D9',
		},
		error: {
			main: colors['primary-red'], // '#bb2828',

			// The color is not taken from the SCSS file because during the production build the value '#FF0000' is replaced with 'red'
			light: '#FF0000',
		},
		blue: {
			main: colors['pure-blue'], // '#1774fe',
			light: '',
			dark: colors['pure-blue'], // '#1774fe',
			contrastText: colors['pure-white'], // '#FFFFFF',
		},
		header: {
			main: colors['pure-white'], // '#FFFFFF',
			light: '',
			dark: colors['secondary'], // '#FEC123',
			contrastText: colors['main-text'], //#635533
		},
		footer: {
			main: colors['light-yellow'], // '#FFFAED',
			light: '',
			dark: colors['secondary'], // '#FEC123',
			contrastText: colors['main-text'], //#635533
		},
		price: {
			main: colors['primary-green'], // '#18A900',
		},
		navbarButton: {
			main: colors['pure-black'], // '#000000',
		},
		navbarContrast: {
			main: colors['pure-black'], // '#000000',
		},
		navbarUserIcon: {
			main: colors['pure-black'], // '#000000',
		},
		navbarText: {
			primary: colors['main-text'], //#635533
			secondary: colors['primary-dark-blue'], //#252f35,
			disabled: colors['dark-grey'], //#8F8F8F
		},
		insurance: {
			primary: colors['primary'], // '#FE7223',
			secondary: colors['secondary'], // '#FEC123',
			highlight: colors['teal'], // '#02C39A',
			price: colors['primary-green'], // '#18A900',
		},
		alarm: {
			armed: colors['primary-green'], // '#18A900',
			disarmed: colors['secondary'], // '#FEC123',
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 10,
				},
				containedPrimary: ({ theme }) => ({
					':hover': { backgroundColor: alpha(theme.palette.primary.main, 0.8) },
					'&:disabled': {
						backgroundColor: alpha(theme.palette.primary.main, 0.4),
						color: theme.palette.primary.contrastText,
					},
				}),
				containedSecondary: ({ theme }) => ({
					':hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
					'&:disabled': {
						backgroundColor: alpha(theme.palette.secondary.main, 0.4),
						color: theme.palette.secondary.contrastText,
					},
				}),
				textSecondary: ({ theme }) => ({
					':hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.2) },
				}),
				outlinedPrimary: ({ theme }) => ({
					':hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
					borderColor: theme.palette.primary.main,
				}),
				contained: ({ theme, ownerState: { color } }) => ({
					'&:disabled': {
						backgroundColor: alpha(
							theme.palette[color && color !== 'inherit' ? color : 'primary']?.main ??
								theme.palette.primary.main,
							0.4
						),
						color: theme.palette[color && color !== 'inherit' ? color : 'primary']?.contrastText,
					},
				}),
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				asterisk: ({ theme }) => ({
					color: theme.palette?.error.main,
					fontSize: theme.typography.h5.fontSize,
					lineHeight: theme.typography.h5.lineHeight,
				}),
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				asterisk: ({ theme }) => ({
					color: theme.palette?.error.main,
					fontSize: theme.typography.h5.fontSize,
					lineHeight: theme.typography.h5.lineHeight,
				}),
			},
		},
	},
	spacing: 8,
});

export const theme: ThemeOptions = {
	...partialTheme,
	typography: {
		...partialTheme.typography,
		fontFamily: 'Noto Sans, sans-serif',
		h1: {
			fontSize: '3.625rem',
			lineHeight: '4.938rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '1.375rem',
				lineHeight: '1.875rem',
			},
		},
		h2: {
			fontSize: '2.25rem',
			lineHeight: '3.063rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '1.25rem',
				lineHeight: '1.563rem',
			},
		},
		h3: {
			fontSize: '1.75rem',
			lineHeight: '2.75rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '1.12rem',
				lineHeight: '1.5rem',
			},
		},
		h4: {
			fontSize: '1.25rem',
			lineHeight: '1.688rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '1rem',
				lineHeight: '1.375rem',
			},
		},
		body1: {
			fontSize: '1rem',
			lineHeight: '1.375rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '0.875rem',
				lineHeight: '1.5rem',
			},
		},
		h5: {
			fontSize: '0.875rem',
			lineHeight: '1.188rem',
			[partialTheme.breakpoints.down('sm')]: {
				fontSize: '0.75rem',
				lineHeight: '1rem',
			},
		},
	},
};
